import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { 
  ClipboardCheck, Clock, AlertCircle, CheckCircle2, 
  AlertTriangle, ChevronRight, X, ExternalLink, Target, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { DESIGN } from '../DesignSystem';
import { Modal } from './FocusModals';

// ================================================
// UTILITY FUNCTIONS
// ================================================
export const getSLAColor = (status) => {
  switch (status) {
    case 'on_track': return { bg: DESIGN.colors.successSubtle, text: DESIGN.colors.success, glow: '0 0 15px rgba(16, 185, 129, 0.3)' };
    case 'warning': return { bg: DESIGN.colors.amberSubtle, text: '#D97706', glow: '0 0 15px rgba(245, 158, 11, 0.3)' };
    case 'breached': return { bg: DESIGN.colors.errorSubtle, text: DESIGN.colors.error, glow: '0 0 15px rgba(239, 68, 68, 0.3)' };
    default: return { bg: DESIGN.colors.background, text: DESIGN.colors.textMuted, glow: 'none' };
  }
};

// ================================================
// REVIEW DECISION MODAL
// ================================================
export const ReviewDecisionModal = ({ isOpen, onClose, project, onSubmit, type }) => {
  const [comments, setComments] = useState('');
  const [loading, setLoading] = useState(false);

  const isApproval = type === 'approved';
  const title = isApproval ? 'Approve Project' : 'Request Revisions';
  const buttonText = isApproval ? 'Approve' : 'Request Revisions';
  const buttonColor = isApproval ? DESIGN.colors.success : DESIGN.colors.amber;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit({
        project_id: project?.id,
        decision: type,
        comments,
        requested_changes: !isApproval ? comments : undefined
      });
      toast.success(isApproval ? 'Project approved!' : 'Revisions requested');
      setComments('');
      onClose();
    } catch (err) {
      toast.error('Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  if (!project) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div 
          className="p-4 rounded-xl"
          style={{ backgroundColor: DESIGN.colors.background }}
        >
          <h4 className="text-base font-bold mb-1" style={{ color: DESIGN.colors.textPrimary }}>
            {project.name}
          </h4>
          <p className="text-sm" style={{ color: DESIGN.colors.textMuted }}>
            {project.description}
          </p>
        </div>

        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: DESIGN.colors.textPrimary }}>
            {isApproval ? 'Comments (optional)' : 'Requested Changes *'}
          </label>
          <Textarea 
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder={isApproval ? 'Add any comments...' : 'Describe the changes needed...'}
            rows={4}
            className="rounded-xl"
            required={!isApproval}
          />
        </div>

        <div className="flex justify-end gap-3 pt-3">
          <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={loading || (!isApproval && !comments)}
            className="rounded-xl gap-2"
            style={{ 
              background: `linear-gradient(135deg, ${buttonColor} 0%, ${buttonColor}DD 100%)`,
              boxShadow: `0 0 20px ${buttonColor}40`
            }}
          >
            {isApproval ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
            {loading ? 'Processing...' : buttonText}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// ================================================
// REASSIGN REVIEWER MODAL
// ================================================
export const ReassignReviewerModal = ({ isOpen, onClose, project, reviewers, onSubmit }) => {
  const [selectedReviewer, setSelectedReviewer] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedReviewer) {
      toast.error('Please select a reviewer');
      return;
    }
    
    setLoading(true);
    try {
      await onSubmit({
        project_id: project?.id,
        new_reviewer_id: selectedReviewer,
        reason
      });
      toast.success('Reviewer reassigned');
      setSelectedReviewer('');
      setReason('');
      onClose();
    } catch (err) {
      toast.error('Failed to reassign reviewer');
    } finally {
      setLoading(false);
    }
  };

  if (!project) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Reassign Reviewer">
      <form onSubmit={handleSubmit} className="space-y-5">
        <div 
          className="p-4 rounded-xl"
          style={{ backgroundColor: DESIGN.colors.background }}
        >
          <h4 className="text-base font-bold mb-1" style={{ color: DESIGN.colors.textPrimary }}>
            {project.name}
          </h4>
          {project.current_reviewer && (
            <p className="text-sm" style={{ color: DESIGN.colors.textMuted }}>
              Current: {project.current_reviewer.name}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: DESIGN.colors.textPrimary }}>
            New Reviewer *
          </label>
          <div className="grid grid-cols-2 gap-3 max-h-48 overflow-y-auto">
            {reviewers?.map(reviewer => (
              <button
                key={reviewer.id}
                type="button"
                onClick={() => setSelectedReviewer(reviewer.id)}
                className="flex items-center gap-3 p-3 rounded-xl text-left transition-all"
                style={{ 
                  backgroundColor: selectedReviewer === reviewer.id ? DESIGN.colors.primarySubtle : DESIGN.colors.background,
                  border: `2px solid ${selectedReviewer === reviewer.id ? DESIGN.colors.primary : 'transparent'}`
                }}
              >
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold text-white"
                  style={{ backgroundColor: selectedReviewer === reviewer.id ? DESIGN.colors.primary : DESIGN.colors.textMuted }}
                >
                  {reviewer.avatar}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate" style={{ color: DESIGN.colors.textPrimary }}>
                    {reviewer.name}
                  </p>
                  <p className="text-xs truncate" style={{ color: DESIGN.colors.textMuted }}>
                    {reviewer.pending_reviews || 0} pending
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: DESIGN.colors.textPrimary }}>
            Reason (optional)
          </label>
          <Textarea 
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why are you reassigning this review?"
            rows={2}
            className="rounded-xl"
          />
        </div>

        <div className="flex justify-end gap-3 pt-3">
          <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={loading || !selectedReviewer}
            className="rounded-xl gap-2"
            style={{ 
              background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
              boxShadow: DESIGN.shadows.glowPrimary
            }}
          >
            {loading ? 'Reassigning...' : 'Reassign'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// ================================================
// EXECUTIVE REVIEW CARD
// ================================================
export const ExecutiveReviewCard = ({ project, onApprove, onRequestRevisions, onReassign, onViewProject }) => {
  const navigate = useNavigate();
  const slaConfig = getSLAColor(project.sla_status);
  
  const statusConfigs = {
    'pending_review': { label: 'Pending Review', color: DESIGN.colors.amber, bg: DESIGN.colors.amberSubtle },
    'in_review': { label: 'In Review', color: DESIGN.colors.primary, bg: DESIGN.colors.primarySubtle },
    'approved': { label: 'Approved', color: DESIGN.colors.success, bg: DESIGN.colors.successSubtle },
    'revisions_requested': { label: 'Revisions', color: DESIGN.colors.error, bg: DESIGN.colors.errorSubtle }
  };
  const statusConfig = statusConfigs[project.review_status] || statusConfigs.pending_review;

  return (
    <div 
      className="rounded-2xl p-5 transition-all hover:shadow-lg group"
      style={{ 
        backgroundColor: DESIGN.colors.card,
        borderLeft: `4px solid ${statusConfig.color}`,
        boxShadow: DESIGN.shadows.md
      }}
      data-testid={`review-card-${project.id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <h4 
              className="text-base font-bold truncate cursor-pointer hover:text-teal-600 transition-colors"
              style={{ color: DESIGN.colors.textPrimary }}
              onClick={() => onViewProject(project)}
            >
              {project.name}
            </h4>
            <span 
              className="text-xs font-bold px-3 py-1 rounded-full flex-shrink-0"
              style={{ backgroundColor: statusConfig.bg, color: statusConfig.color }}
            >
              {statusConfig.label}
            </span>
          </div>
          <p className="text-sm line-clamp-2" style={{ color: DESIGN.colors.textSecondary }}>
            {project.description}
          </p>
        </div>
      </div>

      {/* SLA & Timing */}
      <div className="flex items-center gap-4 mb-4">
        <div 
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
          style={{ backgroundColor: slaConfig.bg }}
        >
          <Clock className="w-4 h-4" style={{ color: slaConfig.text }} />
          <span className="text-xs font-semibold" style={{ color: slaConfig.text }}>
            {project.sla_hours_remaining}h remaining
          </span>
        </div>
        {project.current_reviewer && (
          <div className="flex items-center gap-2">
            <div 
              className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold text-white"
              style={{ backgroundColor: DESIGN.colors.primary }}
            >
              {project.current_reviewer.avatar}
            </div>
            <span className="text-xs font-medium" style={{ color: DESIGN.colors.textSecondary }}>
              {project.current_reviewer.name}
            </span>
          </div>
        )}
      </div>

      {/* Progress */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-semibold" style={{ color: DESIGN.colors.textMuted }}>Progress</span>
          <span className="text-xs font-bold" style={{ color: DESIGN.colors.primary }}>
            {project.completion_percentage}%
          </span>
        </div>
        <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#E2E8F0' }}>
          <div 
            className="h-full rounded-full transition-all"
            style={{ 
              width: `${project.completion_percentage}%`,
              background: `linear-gradient(90deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryLight} 100%)`
            }}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-3 border-t" style={{ borderColor: DESIGN.colors.borderSubtle }}>
        <Button
          size="sm"
          onClick={() => onApprove(project)}
          className="flex-1 rounded-xl gap-1.5"
          style={{ 
            background: `linear-gradient(135deg, ${DESIGN.colors.success} 0%, #059669 100%)`,
            boxShadow: '0 0 15px rgba(16, 185, 129, 0.3)'
          }}
        >
          <CheckCircle2 className="w-4 h-4" />
          Approve
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onRequestRevisions(project)}
          className="flex-1 rounded-xl gap-1.5"
        >
          <AlertTriangle className="w-4 h-4" />
          Revisions
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onReassign(project)}
          className="rounded-xl"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// ================================================
// EXECUTIVE REVIEW SECTION
// ================================================
export const ExecutiveReviewSection = ({ 
  projects, 
  slaSummary, 
  onApprove, 
  onRequestRevisions, 
  onReassign, 
  onViewProject,
  onRefresh,
  loading 
}) => {
  if (loading) {
    return (
      <div 
        className="rounded-2xl p-6"
        style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.lg }}
      >
        <div className="flex items-center gap-3 mb-5">
          <div 
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: DESIGN.colors.primarySubtle }}
          >
            <ClipboardCheck className="w-5 h-5" style={{ color: DESIGN.colors.primary }} />
          </div>
          <div>
            <h3 className="text-base font-bold" style={{ color: DESIGN.colors.textPrimary }}>Executive Reviews</h3>
            <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>Loading...</p>
          </div>
        </div>
        <div className="space-y-4">
          {[1, 2].map(i => (
            <div key={i} className="h-40 rounded-xl animate-pulse" style={{ backgroundColor: DESIGN.colors.background }} />
          ))}
        </div>
      </div>
    );
  }

  if (!projects || projects.length === 0) {
    return (
      <div 
        className="rounded-2xl p-6 text-center"
        style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.lg }}
      >
        <div 
          className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
          style={{ backgroundColor: DESIGN.colors.successSubtle }}
        >
          <CheckCircle2 className="w-7 h-7" style={{ color: DESIGN.colors.success }} />
        </div>
        <h3 className="text-base font-bold mb-1" style={{ color: DESIGN.colors.textPrimary }}>All Caught Up!</h3>
        <p className="text-sm" style={{ color: DESIGN.colors.textMuted }}>No pending reviews</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="executive-review-section">
      {/* SLA Summary Bar */}
      {slaSummary && (
        <div 
          className="flex items-center gap-4 p-4 rounded-2xl"
          style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.md }}
        >
          {[
            { label: 'On Track', value: slaSummary.on_track, color: DESIGN.colors.success },
            { label: 'Warning', value: slaSummary.warning, color: DESIGN.colors.amber },
            { label: 'Breached', value: slaSummary.breached, color: DESIGN.colors.error }
          ].map((stat, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div 
                className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold"
                style={{ backgroundColor: `${stat.color}15`, color: stat.color }}
              >
                {stat.value}
              </div>
              <span className="text-xs font-medium" style={{ color: DESIGN.colors.textMuted }}>
                {stat.label}
              </span>
            </div>
          ))}
          <div className="flex-1" />
          <button 
            onClick={onRefresh}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <RefreshCw className="w-4 h-4" style={{ color: DESIGN.colors.textMuted }} />
          </button>
        </div>
      )}

      {/* Review Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {projects.map(project => (
          <ExecutiveReviewCard
            key={project.id}
            project={project}
            onApprove={onApprove}
            onRequestRevisions={onRequestRevisions}
            onReassign={onReassign}
            onViewProject={onViewProject}
          />
        ))}
      </div>
    </div>
  );
};

export default { 
  getSLAColor, ReviewDecisionModal, ReassignReviewerModal, 
  ExecutiveReviewCard, ExecutiveReviewSection 
};
